import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../hooks/useSEO";

export default function ServiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", birth_date: "", birth_time: "", birth_place: "", focus_area: "",
    partner_name: "", partner_birth_date: "", partner_birth_time: "", partner_birth_place: "",
  });

  useSEO(
    service ? `${service.name} | NAKSRA` : "Report | NAKSRA",
    service ? service.description : "Vedic astrology report from NAKSRA."
  );

  useEffect(() => {
    api
      .get("/services")
      .then((r) => {
        const s = r.data.find((x) => x.id === id);
        if (s) setService(s);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", { service_id: id, ...form });
      const { data: checkout } = await api.post("/payments/checkout", {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (notFound)
    return (
      <div className="min-h-screen bg-[#050507] text-[#F8F9FA]">
        <Navbar />
        <main className="mx-auto max-w-3xl px-6 py-32 text-center">
          <h1 className="font-heading text-4xl">Service not found</h1>
          <Link to="/services" data-testid="back-to-services" className="mt-6 inline-block text-[#D4AF37] hover:text-[#FBE18D]">Back to reports</Link>
        </main>
      </div>
    );

  if (!service)
    return (
      <div className="min-h-screen bg-[#050507] text-[#F8F9FA]">
        <Navbar />
        <main className="mx-auto max-w-7xl px-6 py-32 text-[#A1A1AA]">Loading…</main>
      </div>
    );

  const inputCls =
    "w-full rounded-lg border border-[#27272A] bg-[#050507] px-4 py-3 text-sm text-white placeholder-[#52525B] outline-none transition-colors duration-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40";

  return (
    <div className="min-h-screen bg-[#050507] text-[#F8F9FA]">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Left: service info */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">{service.sanskrit}</p>
            <h1 className="mt-3 font-heading text-4xl font-medium tracking-tight sm:text-5xl">{service.name}</h1>
            <p className="mt-3 text-lg text-[#A1A1AA]">{service.tagline}</p>
            <p className="mt-6 leading-relaxed text-[#A1A1AA]">{service.description}</p>
            <div className="mt-8 rounded-lg border border-white/10 bg-[#0C0C10] p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">What's included</p>
              <ul className="mt-4 space-y-3">
                {service.includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 text-sm text-[#A1A1AA]">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" /> {inc}
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-6 text-sm text-[#A1A1AA]">
              Verified by a qualified Vedic astrologer · Delivered within {service.delivery_days} days · Money-back guarantee
            </p>
            <p className="mt-8 font-heading text-5xl text-[#D4AF37]">${service.price}</p>
          </div>

          {/* Right: birth details form */}
          <div className="rounded-lg border border-white/10 bg-[#0C0C10] p-8">
            {user === undefined ? null : !user ? (
              <div className="flex h-full flex-col items-start justify-center" data-testid="login-required-block">
                <h2 className="font-heading text-3xl font-medium">Sign in to continue</h2>
                <p className="mt-4 text-sm leading-relaxed text-[#A1A1AA]">
                  Your report is delivered to your NAKSRA account, so we need you signed in before checkout.
                </p>
                <div className="mt-8 flex gap-4">
                  <Link to={`/login?next=/services/${id}`} data-testid="detail-login-btn" className="rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D]">
                    Sign in
                  </Link>
                  <Link to={`/register?next=/services/${id}`} data-testid="detail-register-btn" className="rounded-full border border-white/20 px-8 py-3 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5">
                    Create account
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="birth-details-form">
                <h2 className="font-heading text-3xl font-medium">Your birth details</h2>
                <p className="mt-2 text-sm text-[#A1A1AA]">Exact time and place matter — they set your ascendant and Nakshatra.</p>
                <div className="mt-8 space-y-5">
                  <div>
                    <label htmlFor="full-name" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Full name</label>
                    <input id="full-name" data-testid="full-name-input" required value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="As it should appear on the report" />
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="birth-date" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Date of birth</label>
                      <input id="birth-date" data-testid="dob-input" required type="date" value={form.birth_date} onChange={set("birth_date")} className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="birth-time" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Time of birth</label>
                      <input id="birth-time" data-testid="tob-input" required type="time" value={form.birth_time} onChange={set("birth_time")} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="birth-place" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Place of birth</label>
                    <input id="birth-place" data-testid="pob-input" required value={form.birth_place} onChange={set("birth_place")} className={inputCls} placeholder="City, Country" />
                  </div>
                  <div>
                    <label htmlFor="focus-area" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Focus area (optional)</label>
                    <input id="focus-area" data-testid="focus-area-input" value={form.focus_area} onChange={set("focus_area")} className={inputCls} placeholder="e.g. a relocation decision in 2026" />
                  </div>

                  {service.needs_partner && (
                    <div className="rounded-lg border border-[#D4AF37]/30 bg-[#050507] p-5" data-testid="partner-section">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Partner's details</p>
                      <div className="mt-4 space-y-4">
                        <input data-testid="partner-name-input" required value={form.partner_name} onChange={set("partner_name")} className={inputCls} placeholder="Partner's full name" />
                        <div className="grid gap-4 sm:grid-cols-2">
                          <input data-testid="partner-dob-input" required type="date" value={form.partner_birth_date} onChange={set("partner_birth_date")} className={inputCls} />
                          <input data-testid="partner-tob-input" required type="time" value={form.partner_birth_time} onChange={set("partner_birth_time")} className={inputCls} />
                        </div>
                        <input data-testid="partner-pob-input" required value={form.partner_birth_place} onChange={set("partner_birth_place")} className={inputCls} placeholder="Partner's place of birth" />
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="checkout-submit-button"
                  className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] active:scale-[0.98] disabled:opacity-60"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {submitting ? "Preparing secure checkout…" : `Continue to payment — $${service.price}`}
                </button>
                <p className="mt-4 text-center text-xs text-[#A1A1AA]">Secure payment via Stripe · Money-back guarantee</p>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
